import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/**
 * Styling strategy (highest to lowest specificity):
 *
 * 1. Preset semantic tokens  — set once here, cascade to all PrimeNG components via Aura
 *    (e.g. formField.borderRadius → button, input, select, textarea)
 *    Example: https://github.com/search?q=repo%3Aprimefaces%2Fprimeuix+form.field.border.radius+path%3A%2F%5Epackages%5C%2Fthemes%5C%2Fsrc%5C%2Fpresets%5C%2Faura%5C%2F%2F&type=code
 * 2. Preset component overrides — per-component tokens or `css` blocks inside `components:{}`
 *    when a component needs a value that differs from the semantic cascade
 * 3. extend.ds.* custom tokens — generate `--p-ds-*` CSS vars for use in component SCSS
 *    (e.g. var(--p-ds-border-radius-pill), var(--p-ds-compliant-bg))
 * 4. Component SCSS           — scoped styles using `var(--p-*)` / `var(--font-*)` tokens;
 *    uses ViewEncapsulation.None only when PrimeNG child selectors need overriding
 * 5. styles.scss              — last-resort: cross-component utilities and unlayered
 *    overrides that need higher specificity than @layer primeng
 **/

export const AgdPreset = definePreset(Aura, {
  primitive: {
    blue: {
      50: '#F4F8FF',
      100: '#E8F0FF',
      200: '#C5DAFF',
      300: '#88B3FF',
      400: '#4E8CFF',
      500: '#145FF5',
      600: '#0750E0',
      700: '#0042C5',
      800: '#0038B0',
      900: '#00164F',
      950: '#000B28'
    },
    neutral: {
      50: '#F5F6F8',
      100: '#ECEEF1',
      200: '#D5DAE2',
      300: '#A9B4C3',
      400: '#8391A6',
      500: '#5D6D85',
      600: '#4F5F76',
      700: '#425066',
      800: '#39465A',
      900: '#2F3A4A',
      950: '#1E2230'
    },
    green: {
      50: '#F2F9F2',
      100: '#E5F3E5',
      200: '#BEE3BE',
      300: '#78C478',
      400: '#37A437',
      500: '#007E00',
      600: '#006E00',
      700: '#005D00',
      800: '#005200',
      900: '#004400'
    },
    red: {
      50: '#FFF3F3',
      100: '#FFEBEB',
      200: '#FFCDCD',
      300: '#FF9595',
      400: '#FF5151',
      500: '#DA0000',
      600: '#BF0000',
      700: '#A40000',
      800: '#910000',
      900: '#790000'
    },
    yellow: {
      50: '#FFF8D1',
      100: '#FFF09B',
      200: '#FFD729',
      300: '#E5A900',
      400: '#BA8700',
      500: '#B98711',
      600: '#7A5800',
      700: '#674B00',
      800: '#5B4100',
      900: '#4B3600'
    }
  },
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}'
    },
    borderRadius: '12px',
    focusRing: {
      width: '3px',
      style: 'solid',
      color: 'rgba(20, 95, 245, 0.24)',
      offset: '0'
    },
    formField: {
      borderRadius: '10px'
    },
    content: {
      borderRadius: '8px'
    },
    overlay: {
      select: {
        borderRadius: '8px',
        shadow: '0 4px 16px rgba(0, 0, 0, 0.12)'
      },
      popover: {
        borderRadius: '8px',
        shadow: '0px 2px 6px 2px rgba(0,71,207,0.24)'
      },
      navigation: {
        shadow: '0 4px 16px rgba(0, 0, 0, 0.12)'
      }
    },
    colorScheme: {
      light: {
        primary: {
          color: '{primary.500}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}'
        },
        surface: {
          0: '#FFFFFF',
          50: '{neutral.50}',
          100: '{neutral.100}',
          200: '{neutral.200}',
          300: '{neutral.300}',
          400: '{neutral.400}',
          500: '{neutral.500}',
          600: '{neutral.600}',
          700: '{neutral.700}',
          800: '{neutral.800}',
          900: '{neutral.900}',
          950: '{neutral.950}'
        },
        text: {
          color: '#000000',
          hoverColor: '#000000',
          mutedColor: '{neutral.400}'
        },
        content: {
          borderColor: '{surface.200}',
          hoverBackground: '{surface.100}'
        },
        formField: {
          borderColor: '{surface.500}',
          hoverBorderColor: '{primary.color}',
          focusBorderColor: '{primary.color}',
          invalidBorderColor: '{red.500}',
          disabledBackground: '{surface.100}',
          disabledColor: '{surface.300}',
          placeholderColor: '{surface.400}',
          iconColor: '{surface.500}',
          shadow: 'none'
        },
        highlight: {
          background: '{primary.50}',
          focusBackground: '{primary.100}',
          color: '{primary.700}',
          focusColor: '{primary.800}'
        },
        mask: {
          background: 'rgba(0, 0, 0, 0.25)'
        }
      }
    }
  },
  extend: {
    ds: {
      text: {
        subdued: '{neutral.500}',
        neutral: '{neutral.500}',
        link: '{primary.500}'
      },
      border: {
        strong: '{neutral.500}',
        muted: '{neutral.300}'
      },
      surface: {
        subtle: '{neutral.50}',
        hover: '{neutral.100}',
        nav: '{neutral.800}'
      },
      secondary: {
        bg: '{neutral.100}',
        text: '{neutral.800}',
        hover: '{neutral.200}',
        pressed: '{neutral.300}'
      },
      compliant: {
        bg: '{green.100}',
        text: '{green.800}'
      },
      nonCompliant: {
        bg: '{red.100}',
        text: '{red.800}'
      },
      warning: {
        bg: '{yellow.100}',
        text: '#000000',
        border: '{yellow.200}',
        icon: '{yellow.800}'
      },
      error: {
        bg: '{red.100}',
        text: '#000000',
        border: '{red.200}',
        icon: '{red.500}'
      },
      info: {
        bg: '{blue.100}',
        text: '#000000',
        border: '{primary.200}'
      },
      success: {
        bg: '{green.50}',
        text: '#000000',
        border: '{green.200}',
        icon: '{green.500}'
      },
      delete: {
        color: '{red.500}',
        hover: '{red.600}',
        pressed: '{red.700}'
      },
      primaryLight: '{blue.100}',
      icon: {
        default: '{neutral.600}',
        navSize: '24px'
      },
      shadow: {
        topbar: '0px 1px 3px 1px rgba(0, 71, 207, 0.24)'
      },
      loader: {
        track: '{primary.100}'
      },
      borderRadius: {
        card: '8px',
        chip: '6px',
        pill: '999px'
      }
    }
  },

  components: {
    button: {
      root: {
        secondary: {
          color: '{surface.800}',
          hoverColor: '{surface.900}',
          activeColor: '{surface.950}'
        },
        danger: {
          background: '{red.500}',
          borderColor: '{red.500}',
          color: '#ffffff',
          hoverBackground: '{red.600}',
          hoverBorderColor: '{red.600}',
          activeBackground: '{red.700}',
          activeBorderColor: '{red.700}'
        }
      },
      outlined: {
        primary: {
          hoverBackground: '{primary.50}'
        }
      },
      text: {
        primary: {
          hoverBackground: '{primary.50}'
        }
      },
      css: () => `
        .p-button:disabled {
          opacity: 0.6;
        }
      `
    },
    inputtext: {
      css: ({ dt }) => `
        .p-inputtext:disabled {
          border-color: ${dt('neutral.300')};
        }
      `
    },
    card: {
      root: {
        shadow: '0px 1px 3px 1px rgba(0,71,207,0.24)'
      },
      body: {
        padding: '0'
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            color: '#000'
          },
          subtitle: {
            color: '{surface.600}'
          }
        }
      },
      css: `
        .p-card-content {
          padding: 1.25rem;
        }
      `
    },
    menu: {
      root: {
        background: '{surface.0}',
        color: '{text.color}'
      },
      item: {
        color: '{text.color}'
      }
    },
    dialog: {
      css: ({ dt }) => `
        .p-dialog {
          max-height: 100vh;
          max-width: 100vw;
        }
        .p-dialog-header {
          padding-bottom: 0;
        }
        .p-dialog-footer {
          border-top: 1px solid ${dt('surface.200')};
        }
        .p-dialog-header-icon:focus {
          box-shadow: none;
        }
      `
    },
    tooltip: {
      root: {
        borderRadius: '12px'
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.500}',
            color: '#ffffff'
          }
        }
      }
    },
    datatable: {
      css: `
        .p-datatable .p-datatable-table-container {
          height: var(--ds-table-height);
          overflow: auto;
          border-top: var(--ds-table-border-top);
        }
        .p-datatable-tbody > tr > td {
          word-break: break-word;
        }
        .p-datatable-filter-overlay:has(.custom-multiselect-wrapper) {
          background: transparent;
          border: none;
          box-shadow: none;
          padding: 0;
        }
        .p-datatable-filter-overlay:has(.custom-multiselect-wrapper) .p-datatable-filter-buttonbar {
          display: none;
        }
        .custom-multiselect-wrapper > .p-multiselect {
          border: none;
          background: transparent;
          min-height: 0;
          padding: 0;
          pointer-events: none;
        }
        .custom-multiselect-wrapper .p-multiselect-label-container,
        .custom-multiselect-wrapper .p-multiselect-dropdown {
          display: none;
        }
        .custom-multiselect-wrapper .p-multiselect-overlay {
          pointer-events: auto;
        }
        .p-datatable-loading-icon {
          display: none;
        }
      `,
      headerCell: {
        background: '{surface.50}',
        borderColor: '{surface.500}',
        color: '{text.color}'
      },
      row: {
        background: '{surface.0}',
        stripedBackground: '{surface.50}',
        color: '{text.color}',
        hoverBackground: '{surface.100}'
      },
      bodyCell: {
        padding: '10px 12px'
      }
    },
    multiselect: {
      css: `
        .p-multiselect-option {
          white-space: normal;
          word-break: break-word;
        }
        .p-multiselect-option:has(.column-filter-divider) {
          border-top: 1px solid var(--p-neutral-300);
          margin-top: 0.25rem;
          padding-top: 0.5rem;
        }
        .p-multiselect-trigger {
          display: none;
        }
        .p-fluid .p-multiselect {
          height: 0;
        }
      `
    },
    chip: {
      root: {
        borderRadius: '24px'
      },
      colorScheme: {
        light: {
          root: {
            background: '{primary.100}',
            color: '{primary.500}'
          }
        }
      }
    },
    paginator: {
      navButton: {
        selectedBackground: '{surface.50}',
        selectedColor: '{surface.800}'
      },
      css: `
        .p-paginator {
          justify-content: end;
        }
        .p-paginator-page {
          font: var(--font-body-regular);
        }
      `
    },
    tabs: {
      tablist: {
        background: 'transparent'
      },
      tab: {
        background: 'transparent',
        hoverBackground: 'transparent',
        activeBackground: 'transparent',
        padding: '8px 0',
        borderWidth: '0 0 2px 0',
        borderColor: 'transparent',
        activeBorderColor: '{primary.color}',
        color: '{surface.600}',
        activeColor: '{primary.color}',
        hoverColor: '#000000',
        margin: '0'
      },
      css: `
        .p-tablist-tab-list {
          gap: 1rem;
        }
        .p-tab {
          font: var(--font-h4-semibold);
        }
        .p-tablist-active-bar {
          display: none;
        }
      `
    },
    toast: {
      root: {
        borderRadius: '12px',
        width: '40rem'
      },
      colorScheme: {
        light: {
          success: {
            background: '{green.100}',
            color: '{green.800}'
          },
          error: {
            background: '{red.100}',
            color: '{red.500}'
          }
        }
      },
      css: `
        .p-toast-message {
          box-shadow: 0px 2px 6px 2px rgba(0,71,207,0.24);
        }
        .p-toast-message-success .p-toast-message-icon,
        .p-toast-message-success .p-toast-icon-close {
          color: var(--p-green-800);
        }
      `
    },
    fileupload: {
      root: {
        borderColor: '{surface.500}',
        borderRadius: '12px'
      },
      file: {
        padding: '1rem 0.5rem'
      }
    },
    select: {
      root: {
        borderColor: '{surface.500}',
        background: '{surface.0}',
        color: '{text.color}',
        disabledBackground: '{surface.100}',
        disabledColor: '{surface.300}'
      },
      overlay: {
        background: '{surface.0}',
        color: '{text.color}',
        borderColor: '{neutral.300}'
      },
      option: {
        selectedBackground: '{surface.50}',
        selectedColor: '{surface.800}',
        focusBackground: '{surface.100}',
        focusColor: '{text.color}'
      }
    },
    checkbox: {
      root: {
        borderColor: '{surface.500}',
        width: '1.25rem',
        height: '1.25rem',
        borderRadius: '0'
      },
      icon: {
        size: '0.75rem'
      }
    },
    datepicker: {
      header: {
        background: '{surface.0}',
        color: '{text.color}',
        borderColor: '{surface.500}'
      },
      today: {
        background: '{surface.50}',
        color: '{surface.800}'
      },
      css: ({ dt }) => `
        .p-datepicker .p-datepicker-dropdown {
          background: ${dt('primary.color')};
          border-color: ${dt('primary.color')};
          color: ${dt('primary.contrastColor')};
        }
      `
    },
    autocomplete: {
      css: ({ dt }) => `
        .p-autocomplete .p-autocomplete-dropdown {
          background: ${dt('primary.color')};
          border-color: ${dt('primary.color')};
          color: ${dt('primary.contrastColor')};
        }
      `
    },
    accordion: {
      panel: {
        borderWidth: '0'
      },
      css: ({ dt }) => `
        .p-accordionpanel:last-child > .p-accordioncontent > .p-accordioncontent-content {
          border-end-end-radius: ${dt('content.border.radius')};
          border-end-start-radius: ${dt('content.border.radius')};
        }
      `
    },
    progressbar: {
      root: {
        height: '6px',
        borderRadius: '6px 6px 0 0'
      },
      colorScheme: {
        light: {
          value: {
            background: '{primary.color}'
          }
        }
      }
    },
    popover: {
      root: {
        gutter: '5px'
      },
      content: {
        padding: '0'
      },
      css: `
        .p-popover::before,
        .p-popover::after {
          border: none;
        }
      `
    },
    tag: {
      colorScheme: {
        light: {
          primary: {
            background: '{primary.color}'
          }
        }
      }
    },
    chips: {
      root: {
        borderColor: '{surface.500}'
      },
      colorScheme: {
        light: {
          token: {
            background: '{surface.100}',
            color: '{surface.800}'
          }
        }
      }
    }
  }
});
